import { Component, computed, input } from '@angular/core';
import { FILE_TYPE_SVGS } from 'src/app/common/svgs';
import { FILE_TYPE } from 'src/app/@core/models/enums';
import { IconComponent, IconSize } from 'src/app/@core/components/ui/icon/icon.component';

/** Size in pixels (height) or named size. viewBox is 20×24 so width = height * 20/24. */
export type FileIconSize = IconSize | number;

const FILE_ICON_SIZE_CLASS_MAP: Record<IconSize, string> = {
    xxs: 'h-2 w-2',
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    default: 'h-5 w-5',
    md: 'h-6 w-6',
    lg: 'h-7 w-7',
    xl: 'h-8 w-8',
    xxl: 'h-10 w-10',
};

const FILE_ICON_VIEWBOX_WIDTH = 20;
const FILE_ICON_VIEWBOX_HEIGHT = 24;

@Component({
    selector: 'app-file-icon',
    imports: [IconComponent],
    templateUrl: './file-icon.component.html',
    styleUrl: './file-icon.component.scss',
    host: {
        '[class]': 'hostClasses()',
        '[style.width.px]': 'hostWidthPx()',
        '[style.height.px]': 'hostHeightPx()',
    },
})
export class FileIconComponent {
    type = input<FILE_TYPE | undefined>(undefined);
    size = input<FileIconSize>('sm');
    imageUrl = input<string | undefined>(undefined);

    protected readonly hostClasses = computed(() => {
        const s = this.size();
        if (typeof s === 'number') return 'flex items-center justify-center shrink-0';
        const sizeClass = FILE_ICON_SIZE_CLASS_MAP[s as IconSize] ?? FILE_ICON_SIZE_CLASS_MAP.default;
        return `flex items-center justify-center shrink-0 ${sizeClass}`;
    });

    protected readonly hostWidthPx = computed(() => {
        const s = this.size();
        if (typeof s !== 'number') return null;
        const h = this.getIconSizePx();
        return Math.round((h * FILE_ICON_VIEWBOX_WIDTH) / FILE_ICON_VIEWBOX_HEIGHT);
    });

    protected readonly hostHeightPx = computed(() => {
        const s = this.size();
        return typeof s === 'number' ? this.getIconSizePx() : null;
    });

    /** Named IconSize to pass down to app-icon; null for numeric sizes (icon fills via h-full/w-full). */
    protected readonly namedSize = computed<IconSize | null>(() => {
        const s = this.size();
        return typeof s === 'string' ? (s as IconSize) : null;
    });

    isImageType() {
        const fileType = this.type();
        return fileType === FILE_TYPE.JPG ||
            fileType === FILE_TYPE.JPEG ||
            fileType === FILE_TYPE.PNG;
    }

    getIconSizePx(): number {
        const s = this.size();
        if (typeof s === 'number') return s;
        const pxMap: Record<IconSize, number> = {
            xxs: 8, xs: 12, sm: 16, default: 20, md: 24, lg: 28, xl: 32, xxl: 40,
        };
        return pxMap[s as IconSize] ?? 20;
    }

    getIcon(): string {
        switch (this.type()) {
            case FILE_TYPE.PDF: return FILE_TYPE_SVGS.PDF;
            case FILE_TYPE.WORD: return FILE_TYPE_SVGS.DOCUMENT;
            case FILE_TYPE.TEXT: return FILE_TYPE_SVGS.TEXT;
            case FILE_TYPE.HTML: return FILE_TYPE_SVGS.HTML;
            case FILE_TYPE.PYTHON: return FILE_TYPE_SVGS.PYTHON;
            case FILE_TYPE.MARKDOWN: return FILE_TYPE_SVGS.MARKDOWN;
            case FILE_TYPE.POWERPOINT: return FILE_TYPE_SVGS.POWERPOINT;
            case FILE_TYPE.EXCEL: return FILE_TYPE_SVGS.EXCEL;
            case FILE_TYPE.JSON: return FILE_TYPE_SVGS.JSON;
            default: return FILE_TYPE_SVGS.GENERIC;
        }
    }

    getSvgWithSize(): string {
        const svg = this.getIcon();
        return svg
            .replace(/width="[^"]*"/, 'width="100%"')
            .replace(/height="[^"]*"/, 'height="100%"')
            .replace(/<svg/, '<svg preserveAspectRatio="xMidYMid meet" ');
    }
}
